import type { TSetupGuideId } from '../types';

import { useGSetupGuideContext } from '../contexts/GSetupGuideProvider';
import { useCallback, useEffect, useRef } from 'react';
import { SETUP_GUIDE_CONFIG } from '../constants';
import { createPortal } from 'react-dom';
import { cls } from '~/utils';

export interface GSetupGuideItemWrapperProps {
  setupId: TSetupGuideId;
  variant?: 'default' | 'button' | 'dashed-button';
  wrap?: 'one' | 'three' | 'four';
  disabledClick?: boolean;
  shouldDelay?: boolean;
  /** Stretch the wrapper to its parent's height so a wrapped fill-height child can fill it. */
  fillHeight?: boolean;
  children: React.ReactNode;
}

export const GSetupGuideItemWrapper = ({
  setupId,
  variant = 'default',
  wrap,
  disabledClick,
  shouldDelay = false,
  fillHeight = false,
  children,
}: GSetupGuideItemWrapperProps) => {
  const { highlightedSetupId, handleCloseHighLight } = useGSetupGuideContext();

  const isShowSetupGuide = highlightedSetupId === setupId;

  const targetRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (!targetRef.current || !portalRef.current) return;

    const rect = targetRef.current.getBoundingClientRect();

    const itemOffset =
      variant === 'button'
        ? { width: 4, height: 4, position: 2 }
        : { width: 0, height: 0, position: 0 };

    Object.assign(portalRef.current.style, {
      width: `${rect.width + itemOffset.width}px`,
      height: `${rect.height + itemOffset.height}px`,
      top: `${rect.top - itemOffset.position}px`,
      left: `${rect.left - itemOffset.position}px`,
    });

    const spotlightOverlay = document.querySelector('.spotlight-overlay');

    if (spotlightOverlay instanceof HTMLElement) {
      // Example:
      // | Point         | Meaning                         |
      // | ------------- | ------------------------------- |
      // | `0 0`         | Top-left of the viewport        |
      // | `100vw 0`     | Top-right of the viewport       |
      // | `100vw 100vh` | Bottom-right of the viewport    |
      // | `0 100vh`     | Bottom-left of the viewport     |
      // | `0 0`         | Closing the outer rectangle     |
      // | `150px 100px` | Top-left corner of the hole     |
      // | `350px 100px` | Top-right corner of the hole    |
      // | `350px 200px` | Bottom-right corner of the hole |
      // | `150px 200px` | Bottom-left corner of the hole  |
      // | `150px 100px` | Closing the hole rectangle      |

      const clipPath = `polygon(
        0px 0px,
        100vw 0px,
        100vw 100vh,
        0px 100vh,
        0px 0px,
        ${rect.left}px ${rect.top}px,
        ${rect.left}px ${rect.top + rect.height}px,
        ${rect.left + rect.width}px ${rect.top + rect.height}px,
        ${rect.left + rect.width}px ${rect.top}px,
        ${rect.left}px ${rect.top}px
      )`;

      spotlightOverlay.style.clipPath = clipPath;
    }
  }, [variant]);

  useEffect(() => {
    if (!isShowSetupGuide) return;

    const scrollIntoViewAndUpdatePosition = () => {
      updatePosition();
      targetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    if (shouldDelay) {
      setTimeout(() => {
        scrollIntoViewAndUpdatePosition();
      }, SETUP_GUIDE_CONFIG.ANIMATION_TIME_IN_MS);
    } else {
      scrollIntoViewAndUpdatePosition();
    }
  }, [shouldDelay, isShowSetupGuide, updatePosition]);

  useEffect(() => {
    if (!isShowSetupGuide) return;

    const controller = new AbortController();
    const signal = controller.signal;

    const campaignAdvanceSettingScrollable = document.querySelector(
      '#CampaignAdvanceSetting__Scrollable',
    );

    if (campaignAdvanceSettingScrollable) {
      campaignAdvanceSettingScrollable.addEventListener('scroll', updatePosition, { signal });
      campaignAdvanceSettingScrollable.addEventListener('resize', updatePosition, { signal });
    }

    window.addEventListener('scroll', updatePosition, { signal });
    window.addEventListener('resize', updatePosition, { signal });

    // Closing the panel on tablet reflows the container (width animates, banner above the bar
    // re-wraps), moving the target without a scroll/resize event. Re-sync on its size changes.
    const reflowTarget = targetRef.current?.closest('.GSetupGuide-template-main');
    const resizeObserver = new ResizeObserver(() => updatePosition());
    if (reflowTarget) resizeObserver.observe(reflowTarget);

    return () => {
      controller.abort();
      resizeObserver.disconnect();
    };
  }, [isShowSetupGuide, updatePosition]);

  return (
    <>
      <div
        className={cls('relative', {
          'h-full': fillHeight,
          'flex-[0.2]': wrap === 'one',
          'flex-[0.6]': wrap === 'three',
          'flex-[0.8]': wrap === 'four',
        })}
        ref={targetRef}
        onClick={disabledClick ? undefined : handleCloseHighLight}
      >
        {children}
      </div>

      {targetRef.current &&
        isShowSetupGuide &&
        createPortal(
          <div
            ref={portalRef}
            className={cls('GSetupGuide', {
              'GSetupGuide--button': variant === 'button' || variant === 'dashed-button',
            })}
          />,
          document.body,
        )}
    </>
  );
};
